/* eslint-disable @typescript-eslint/no-explicit-any */
import { type GetLast } from 'src/types';

/**
 * This is a dummy type that represents a generic async function signature
 */
type PromiseCreator<Params extends any[] = any[], ReturnType = any> = (
  ...args: Params
) => Promise<ReturnType>;

/**
 * Defines a recursive Function relationship where the awaited promise Return of each Fn must match the Params of the next
 */
type PipePromisesArgs<
  Creators extends PromiseCreator[],
  Acc extends PromiseCreator[] = [],
> = Creators extends [(...args: infer A) => Promise<infer B>]
  ? [...Acc, (...args: A) => Promise<B>]
  : Creators extends [PromiseCreator, ...infer Tail]
  ? Tail extends [(arg: infer B) => Promise<any>, ...PromiseCreator[]]
    ? PipePromisesArgs<Tail, [...Acc, (...args: any[]) => Promise<B>]>
    : Acc
  : Acc;

/**
 * Pipes a chain of Promise Creators, returning value from final function
 *
 * @param firstFn - first function to be called in piped chain
 * @param fns - remaining arguments are chain of functions to be called in succession, with the awaited promise Return Type of the previous matching Params of the next
 * @returns the Return of the final function in the chain
 */
export const pipePromises =
  <FirstFn extends PromiseCreator, Creators extends PromiseCreator[]>(
    firstFn: FirstFn,
    ...fns: PipePromisesArgs<Creators> extends Creators ? Creators : PipePromisesArgs<Creators>
  ) =>
  (): ReturnType<GetLast<Creators, FirstFn>> =>
    (fns as PromiseCreator[]).reduce((acc, fn) => acc.then(fn), firstFn()) as ReturnType<
      GetLast<Creators, FirstFn>
    >;
